import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { isBetaUnlocked, setBetaUnlocked, verifyBetaCode } from "../lib/betaAccess";
import {
  WAITLIST_MAILTO,
  hasWaitlistKey,
  submitWaitlist,
  type WaitlistEntry,
} from "../lib/waitlist";

// Public landing page + private-beta gate (L0-U5 -- ADR 0024 D1). A visitor must
// enter the shared beta code (verified server-side) before the sign-in
// affordance is revealed. Already-signed-in users skip straight to the
// dashboard. The gate is the public front door; ProtectedRoute funnels
// not-yet-unlocked anonymous visitors here. It is a funnel, not auth: Auth0 +
// the local users row remain the real boundary.
export function HomePage() {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const [unlocked, setUnlocked] = useState(() => isBetaUnlocked());
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);

  // Already signed in: no reason to show the gate.
  if (isAuthenticated) {
    return (
      <Shell>
        <Link to="/dashboard" className="text-primary underline">
          Go to dashboard
        </Link>
      </Shell>
    );
  }

  if (showWaitlist) {
    return (
      <Shell>
        <WaitlistForm onBack={() => setShowWaitlist(false)} />
      </Shell>
    );
  }

  if (!unlocked) {
    const onSubmit = async (event: React.FormEvent) => {
      event.preventDefault();
      const trimmed = code.trim();
      if (trimmed.length === 0) {
        setError("Enter your invite code.");
        return;
      }
      setSubmitting(true);
      setError(null);
      const ok = await verifyBetaCode(trimmed);
      setSubmitting(false);
      if (ok) {
        setBetaUnlocked();
        setUnlocked(true);
      } else {
        setError("That code isn't valid. Check it and try again.");
      }
    };

    return (
      <Shell>
        <p className="max-w-sm text-center text-sm text-on-surface-variant">
          WickScope is in private beta. Enter your invite code to continue.
        </p>
        <form onSubmit={onSubmit} className="flex w-full max-w-sm flex-col gap-3">
          <label htmlFor="beta-code" className="sr-only">
            Invite code
          </label>
          <Input
            id="beta-code"
            name="beta-code"
            type="text"
            autoComplete="off"
            placeholder="Invite code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            aria-invalid={error !== null}
            disabled={submitting}
          />
          {error !== null ? (
            <p role="alert" className="text-sm text-error">
              {error}
            </p>
          ) : null}
          <Button type="submit" disabled={submitting}>
            {submitting ? "Checking..." : "Continue"}
          </Button>
        </form>
        <p className="max-w-sm text-center text-sm text-on-surface-variant">
          Don&apos;t have a code?{" "}
          <WaitlistCta onOpen={() => setShowWaitlist(true)} />
        </p>
      </Shell>
    );
  }

  // Unlocked but not signed in: reveal sign-in + request-access messaging.
  return (
    <Shell>
      <Button
        type="button"
        onClick={() => {
          void loginWithRedirect();
        }}
      >
        Sign in
      </Button>
      <p className="max-w-sm text-center text-sm text-on-surface-variant">
        Don&apos;t have access yet?{" "}
        <WaitlistCta onOpen={() => setShowWaitlist(true)} /> to request a beta
        invite.
      </p>
    </Shell>
  );
}

// Inline CTA: opens the on-page waitlist form when a Web3Forms key is
// configured; otherwise degrades to the pre-filled mailto link.
function WaitlistCta({ onOpen }: { onOpen: () => void }) {
  if (!hasWaitlistKey()) {
    return (
      <a href={WAITLIST_MAILTO} className="text-primary underline">
        Join the waitlist
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      className="text-primary underline"
    >
      Join the waitlist
    </button>
  );
}

function WaitlistForm({ onBack }: { onBack: () => void }) {
  const [entry, setEntry] = useState<WaitlistEntry>({
    email: "",
    name: "",
    markets: "",
  });
  const [status, setStatus] = useState<"idle" | "submitting" | "sent" | "error">(
    "idle",
  );

  if (status === "sent") {
    return (
      <>
        <p className="max-w-sm text-center text-sm text-on-surface-variant">
          You&apos;re on the list — we&apos;ll email your invite when a spot
          opens up.
        </p>
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-primary underline"
        >
          Back
        </button>
      </>
    );
  }

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setStatus("submitting");
    const ok = await submitWaitlist({
      email: entry.email.trim(),
      name: entry.name.trim(),
      markets: entry.markets.trim(),
    });
    setStatus(ok ? "sent" : "error");
  };

  const set =
    (field: keyof WaitlistEntry) =>
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setEntry((prev) => ({ ...prev, [field]: event.target.value }));

  return (
    <>
      <p className="max-w-sm text-center text-sm text-on-surface-variant">
        Join the waitlist and we&apos;ll email your beta invite.
      </p>
      <form onSubmit={onSubmit} className="flex w-full max-w-sm flex-col gap-3">
        <label htmlFor="waitlist-email" className="sr-only">
          Email address
        </label>
        <Input
          id="waitlist-email"
          name="email"
          type="email"
          required
          autoComplete="email"
          placeholder="Email address"
          value={entry.email}
          onChange={set("email")}
          disabled={status === "submitting"}
        />
        <label htmlFor="waitlist-name" className="sr-only">
          Name
        </label>
        <Input
          id="waitlist-name"
          name="name"
          type="text"
          autoComplete="name"
          placeholder="Name (optional)"
          value={entry.name}
          onChange={set("name")}
          disabled={status === "submitting"}
        />
        <label htmlFor="waitlist-markets" className="sr-only">
          Markets you trade
        </label>
        <Input
          id="waitlist-markets"
          name="markets"
          type="text"
          autoComplete="off"
          placeholder="Markets you trade (optional)"
          value={entry.markets}
          onChange={set("markets")}
          disabled={status === "submitting"}
        />
        {status === "error" ? (
          <p role="alert" className="text-sm text-error">
            Something went wrong sending that. Try again, or email{" "}
            <a href={WAITLIST_MAILTO} className="underline">
              support@wickscope.com
            </a>
            .
          </p>
        ) : null}
        <Button type="submit" disabled={status === "submitting"}>
          {status === "submitting" ? "Joining..." : "Join the waitlist"}
        </Button>
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-on-surface-variant underline"
        >
          Back
        </button>
      </form>
    </>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <section className="min-h-screen flex flex-col items-center justify-center gap-4 bg-surface px-4">
      <h1 className="font-display text-4xl font-medium tracking-tight text-on-surface">
        WickScope
      </h1>
      {children}
    </section>
  );
}
